import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import TextBox from "../../common/TextBox";
import TextArea from "../../common/TextArea";
import { Form, Formik } from "formik";
import Button from "../../common/Button";
import ProfileContext from "../ProfileContext";
import { CreateInput } from "thai-address-autocomplete-react";
import ModalUpload from "./UploadModal.jsx";

const InputThaiAddress = CreateInput();

const googleClientId = import.meta.env.GOOGLE_CLIENT_ID;
const lineLinkClientId = import.meta.env.LINE_LINK_CLIENT_ID;
const facebookAppId = import.meta.env.FACEBOOK_APP_ID;

// * SVGs
import { ReactComponent as EmailSvg } from "../../../assets/icons/email.svg";
import { ReactComponent as GoogleSvg } from "../../../assets/icons/google.svg";
import { ReactComponent as FacebookSvg } from "../../../assets/icons/facebook.svg";
import { ReactComponent as LineSvg } from "../../../assets/icons/line.svg";
import { ReactComponent as WeChatSvg } from "../../../assets/icons/wechat.svg";
import { ReactComponent as ShuffleSvg } from "../../../assets/icons/shuffle.svg";
import { ReactComponent as LinkSvg } from "../../../assets/icons/link.svg";
import { ReactComponent as UnlinkSvg } from "../../../assets/icons/unlink.svg";
import { PAGE_STATE } from "../../../constants";
import FileUploader from "../../common/FileUploader";
import GoogleLogin from "react-google-login";
import liff from "@line/liff";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import useUsers from "../../../hooks/useUsers";
import { toast } from "react-toastify";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { isValidNumber } from "../../../helpers/common.helper";

const assetsUri = import.meta.env.ASSETS_URI;

const EditProfile = () => {
  const { user, currentPlan, setUser } = useContext(ProfileContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [avatar, setAvatar] = useState(undefined);
  const [toggleModal, setToggleModal] = useState(false);
  const [isAvatarChanged, setIsAvatarChanged] = useState(false);
  const { patchUser, queryUsers, uploadAvatar, getUser } = useUsers();
  const {
    setUser: appSetUser,
    getLang,
    plans,
    refetchUser,
  } = useContext(AppContext);
  const Translation = getLang(langComponent.profile);
  const TranslationCommon = getLang(langComponent.common);
  const navigate = useNavigate();
  const formikRef = useRef(null);

  liff.init({ liffId: lineLinkClientId });

  useEffect(() => {
    if (!user || !currentPlan) return;

    setPageState(PAGE_STATE.IDLE);
    setAvatar(user.avatar);

    setAddr({
      district: user?.contact?.address?.subDistrict,
      amphoe: user?.contact?.address?.district,
      province: user?.contact?.address?.province,
      zipcode: user?.contact?.address?.postalCode,
    });
  }, [user, currentPlan]);

  const [addr, setAddr] = useState({
    district: user?.contact?.address?.subDistrict,
    amphoe: user?.contact?.address?.district,
    province: user?.contact?.address?.province,
    zipcode: user?.contact?.address?.postalCode,
  });

  const handleAddressChange = (scope) => (value) => {
    setAddr((oldAddr) => ({
      ...oldAddr,
      [scope]: value,
    }));
  };

  const handleAddressSelect = (address) => {
    setAddr(address);
  };

  useEffect(() => {
    if (!formikRef.current) return;

    if (!addr.amphoe && !addr.district && !addr.province && !addr.zipcode) {
      formikRef.current.setFieldError("location", "");
      return;
    }

    if (!addr.amphoe || !addr.district || !addr.province || !addr.zipcode) {
      formikRef.current.setFieldError(
        "location",
        "Please specify all locations"
      );
      return;
    }

    formikRef.current.setFieldError("location", "");
  }, [addr]);

  // * Handle avatar upload
  const handleFileChange = async (files) => {
    setToggleModal(false);
    if (!files || files === undefined) return;
    await uploadAvatar(user._id, files);
    const _user = await getUser(user._id);

    setAvatar(_user.avatar);
    setIsAvatarChanged(true);
    appSetUser((u) => ({ ...u, avatar: _user.avatar }));
    toast.success("Avatar saved");
  };

  const handleViewClose = () => {
    setToggleModal(false);
  };

  // * ====== Social Login Handling ======
  const handleGoogleSignInSuccess = async (e) => {
    try {
      const { googleId } = e;
      const findUser = await queryUsers({ googleId: googleId }, null, null, {
        _id: 1,
      });
      if (findUser.data.length !== 0) {
        toast.error("Google Id already exist");
        return;
      }
      const patchResult = await patchUser(user._id, { googleId: googleId });
      setUser(patchResult);
    } catch (error) {
      console.error("handleGoogleSignInSuccess", error);
    }
  };

  const handleGoogleSignInFailure = (e) => {
    console.error(e);
  };

  const handleUnLinkGoogle = async () => {
    try {
      if (!user.googleId) return;
      const isCanUnLink = checkRequrieOneLinkAccount();
      if (!isCanUnLink) {
        toast.warn("Account requrie 1 link");
        return;
      }
      const patchResult = await patchUser(user._id, { googleId: null });
      setUser(patchResult);
    } catch (error) {
      console.error("handleUnLinkGoogle", error);
    }
  };

  const handleLinkLine = async () => {
    try {
      liff.login();
    } catch (error) {
      console.error("handleLinkLine", error);
    }
  };

  const handleUnLinkLine = async () => {
    try {
      if (!user.lineId) return;
      const isCanUnLink = checkRequrieOneLinkAccount();
      if (!isCanUnLink) {
        toast.warn("Account requrie 1 link");
        return;
      }
      const patchResult = await patchUser(user._id, { lineId: null });
      setUser(patchResult);
    } catch (error) {
      console.error("handleUnLinkLine", error);
    }
  };

  const handleLinkFacebook = async (e) => {
    try {
      const { userID } = e;
      const findUser = await queryUsers({ facebookId: userID }, null, null, {
        _id: 1,
      });

      if (findUser.data.length !== 0) {
        toast.error("Facebook User already linked with another account!");
        return;
      }

      const patchResult = await patchUser(user._id, { facebookId: userID });
      toast.success("Facebook Linked");

      setUser(patchResult);
    } catch (error) {
      console.error("handleLinkFacebook", error);
    }
  };

  const handleUnLinkFacebook = async () => {
    try {
      if (!user.facebookId) return;
      const isCanUnLink = checkRequrieOneLinkAccount();
      if (!isCanUnLink) {
        toast.warn("Account requrie 1 link");
        return;
      }
      const patchResult = await patchUser(user._id, { facebookId: null });
      setUser(patchResult);
      toast.success("Facebook Unlinked");
    } catch (error) {
      console.error("handleUnLinkFacebook", error);
    }
  };

  const handleCancelClick = () => {
    navigate("/profile");
  };

  const handleValidate = (values) => {
    const {
      firstname,
      surname,
      bio,
      streetAddress,
      apt,
      subDistrict,
      district,
      province,
      postalCode,
    } = values;

    let errors = {};

    // * Validate address section if only a field in the section is specified
    if (streetAddress || apt) {
      // * Leave out apt since it's optional
      if (!streetAddress) errors.streetAddress = "Cannot be blank";

      if (!addr.amphoe || !addr.district || !addr.province || !addr.zipcode) {
        errors.location = "Please specify all locations";
      }
    }

    return errors;
  };

  const handleSubmit = async ({
    firstname,
    surname,
    bio,
    streetAddress,
    apt,
  }) => {
    const result = await patchUser(user._id, {
      firstname,
      surname,
      bio,
      contact: {
        address: {
          streetAddress,
          apt,
          subDistrict: addr.district,
          district: addr.amphoe,
          province: addr.province,
          postalCode: addr.zipcode,
        },
      },
    });

    toast.success("Profile saved");
    appSetUser((u) => ({ ...u, firstname, surname }));
    setUser(result);
    navigate("/profile");
  };

  const checkRequrieOneLinkAccount = () => {
    const { email, googleId, lineId, facebookId } = user;
    let accounts = [];
    if (email) accounts.push("email");
    if (googleId) accounts.push("google");
    if (lineId) accounts.push("line");
    if (facebookId) accounts.push("facebook");
    if (accounts.length > 1) return true;
    return false;
  };

  const handleChangePlanClick = () => {
    // * Check if is company
    navigate("/change-plan");
  };

  const handleLeaveCompany = async () => {
    const _guestPlan = plans.find((p) => p.name === "Guest");

    await patchUser(user._id, {
      plan: _guestPlan._id,
      roles: [],
      company: null,
    });

    refetchUser();

    navigate("/profile");
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <></>;
  }

  const {
    firstname,
    surname,
    bio,
    contact,
    email,
    googleId,
    lineId,
    facebookId,
  } = user;

  const isLeaveCompany = user.company && currentPlan.name === "Business";

  return (
    <div className="profile-edit-profile">
      <div className="profile-edit-profile-avatar-section">
        <img
          className="profile-edit-profile-avatar-section__avatar"
          src={`${assetsUri}/${avatar}`}
          alt="avatar"
          onError={({ target }) => {
            target.src = avatar;
          }}
        />
        <Button
          id="avatar"
          label={Translation("Upload picture")}
          onClick={() => setToggleModal(true)}
        />
      </div>
      <div className="profile-edit-profile-info-section">
        <Formik
          enableReinitialize={true}
          initialValues={{
            email: email || "",
            firstname,
            surname,
            bio,
            plan: currentPlan.name,
            streetAddress: contact?.address?.streetAddress || "",
            apt: contact?.address?.apt || "",
            googleId: googleId || "",
            lineId: lineId || "",
            facebookId: facebookId || "",
          }}
          validate={handleValidate}
          onSubmit={handleSubmit}
          innerRef={formikRef}
        >
          {({
            values: { plan, email, googleId, facebookId, lineId, wechatId },
            errors,
          }) => (
            <Form>
              <div className="profile-edit-profile-info-section__section profile-edit-profile-info-section-info">
                <h3 className="profile-edit-profile-info-section-header">
                  {Translation("Information")}
                </h3>
                <TextBox name="firstname" label={Translation("First name")} />
                <TextBox name="surname" label={Translation("Last name")} />
                <TextArea
                  name="bio"
                  label={Translation("Bio")}
                  placeholder={Translation("Enter you bio")}
                />
                <div className="profile-edit-profile-plan">
                  <div className="profile-edit-profile-plan__title">
                    {Translation("Your plan")}
                  </div>
                  <div className="profile-edit-profile-plan-content">
                    <div className="profile-edit-profile-plan-content__value">
                      {plan}
                    </div>
                    <div className="profile-edit-profile-plan-content__action">
                      <div
                        className="profile-edit-profile-info-section__trailing-icon"
                        onClick={
                          isLeaveCompany
                            ? handleLeaveCompany
                            : handleChangePlanClick
                        }
                      >
                        {isLeaveCompany ? (
                          <>
                            <span style={{ fontSize: 14 }}>
                              {Translation("Leave company")}
                            </span>
                          </>
                        ) : (
                          <>
                            <ShuffleSvg />
                            <span style={{ fontSize: 14 }}>
                              {Translation("Change")}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="profile-edit-profile-info-section__section profile-edit-profile-info-section-address">
                <h3 className="profile-edit-profile-info-section-header">
                  {Translation("Address")}
                </h3>
                <TextBox
                  name="streetAddress"
                  label={Translation("Street Address")}
                  className="profile-edit-profile-info-section-address__street-address"
                />
                <TextBox
                  name="apt"
                  label={"Apt, suite, etc (Optional)"}
                  className="profile-edit-profile-info-section-address__apt"
                />
                <div className="address-textbox">
                  <label className="address-textbox__title">
                    {Translation("Sub District (Thai)")}
                  </label>
                  <InputThaiAddress.District
                    value={addr["district"]}
                    onChange={handleAddressChange("district")}
                    onSelect={handleAddressSelect}
                    className="address-textbox__input"
                  />
                </div>
                <div className="address-textbox">
                  <label className="address-textbox__title">
                    {Translation("District (Thai)")}
                  </label>
                  <InputThaiAddress.Amphoe
                    value={addr["amphoe"]}
                    onChange={handleAddressChange("amphoe")}
                    onSelect={handleAddressSelect}
                    className="address-textbox__input"
                  />
                </div>
                <div className="address-textbox">
                  <label className="address-textbox__title">
                    {Translation("Province (Thai)")}
                  </label>
                  <InputThaiAddress.Province
                    value={addr["province"]}
                    onChange={handleAddressChange("province")}
                    onSelect={handleAddressSelect}
                    className="address-textbox__input"
                  />
                </div>
                <div className="address-textbox">
                  <label className="address-textbox__title">
                    {Translation("Postal Code")}
                  </label>
                  <InputThaiAddress.Zipcode
                    value={addr["zipcode"]}
                    onChange={handleAddressChange("zipcode")}
                    onSelect={handleAddressSelect}
                    className="address-textbox__input"
                  />
                </div>
                {errors.location && (
                  <span
                    className="address-textbox__error"
                    style={{ columnSpan: 2 }}
                  >
                    {errors.location}
                  </span>
                )}
              </div>
              <div className="profile-edit-profile-info-section__section profile-edit-profile-info-section-account">
                <h3 className="profile-edit-profile-info-section-header">
                  {Translation("Account")}
                </h3>
                <TextBox
                  name="email"
                  label="Email"
                  disabled
                  placeholder={!email ? Translation("Not linked") : ""}
                  leadingIcon={<EmailSvg />}
                  trailingIcon={
                    <div className="profile-edit-profile-info-section__trailing-icon">
                      {email ? <UnlinkSvg /> : <LinkSvg />}
                      <span>
                        {email ? Translation("Unlink") : Translation("Link")}
                      </span>
                    </div>
                  }
                />
                <TextBox
                  name="googleId"
                  label="Google"
                  disabled
                  placeholder={!googleId ? Translation("Not linked") : ""}
                  leadingIcon={<GoogleSvg style={{ width: "18px" }} />}
                  trailingIcon={
                    <GoogleLogin
                      clientId={googleClientId}
                      onSuccess={handleGoogleSignInSuccess}
                      onFailure={handleGoogleSignInFailure}
                      cookiePolicy={"single_host_origin"}
                      render={(renderProps) => (
                        <div
                          className="profile-edit-profile-info-section__trailing-icon"
                          onClick={
                            googleId ? handleUnLinkGoogle : renderProps.onClick
                          }
                        >
                          {googleId ? <UnlinkSvg /> : <LinkSvg />}
                          <span>
                            {googleId
                              ? Translation("Unlink")
                              : Translation("Link")}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <TextBox
                  name="facebookId"
                  label="Facebook"
                  disabled
                  placeholder={!facebookId ? Translation("Not linked") : ""}
                  leadingIcon={<FacebookSvg style={{ width: "18px" }} />}
                  trailingIcon={
                    <FacebookLogin
                      appId={facebookAppId}
                      autoLoad={false}
                      fields="name,email,picture"
                      size="small"
                      callback={
                        facebookId ? handleUnLinkFacebook : handleLinkFacebook
                      }
                      render={(renderProps) => (
                        <div
                          className="profile-edit-profile-info-section__trailing-icon"
                          onClick={
                            facebookId
                              ? handleUnLinkFacebook
                              : renderProps.onClick
                          }
                        >
                          {facebookId ? <UnlinkSvg /> : <LinkSvg />}
                          <span>
                            {facebookId
                              ? Translation("Unlink")
                              : Translation("Link")}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <TextBox
                  name="lineId"
                  label="Line"
                  disabled
                  placeholder={!lineId ? Translation("Not linked") : ""}
                  leadingIcon={<LineSvg style={{ width: "18px" }} />}
                  trailingIcon={
                    <div
                      className="profile-edit-profile-info-section__trailing-icon"
                      onClick={lineId ? handleUnLinkLine : handleLinkLine}
                    >
                      {lineId ? <UnlinkSvg /> : <LinkSvg />}
                      <span>
                        {lineId ? Translation("Unlink") : Translation("Link")}
                      </span>
                    </div>
                  }
                />
              </div>
              <div className="profile-edit-profile-info-section-cta">
                <Button label={TranslationCommon("Save")} type="submit" />
                <Button
                  variant="secondary"
                  label={TranslationCommon("Cancel")}
                  onClick={handleCancelClick}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {toggleModal && (
        <ModalUpload
          aspectRatio={1}
          onClose={handleViewClose}
          onSuccess={handleFileChange}
          circularCrop={true}
        />
      )}
    </div>
  );
};

EditProfile.propTypes = {};

export default EditProfile;
